import React from 'react';
import { createPortal } from 'react-dom';
import Dropdown, { DropdownOption } from '../../../../components/common/Dropdown';
import ErrorBanner, { ValidationError } from '../../../../components/common/ErrorBanner';

const ValidationErrorDisplay = React.memo<{ message: string; id: string }>(({ message, id }) => (
  <div className="error-description">
    <ul id={id}>
      <li>
        <img
          src="/assets/images/errorCircleSmall.svg"
          alt="Error icon"
          width="14"
          height="14"
        />
        <span className="screenreader-only">{message}</span> {message}
      </li>
    </ul>
  </div>
));

ValidationErrorDisplay.displayName = 'ValidationErrorDisplay';

const getRemainingCharacterText = (value: string, maxLength: number): string => (
  `${Math.max(0, maxLength - value.length)} of ${maxLength} character left`
);

export interface ManageTestOfChangeProps {
  visible: boolean;
  validationErrors: ValidationError[];
  titleRef: React.RefObject<HTMLInputElement>;
  tocTitle: string;
  setTocTitle: (s: string) => void;
  tocTextRef: React.RefObject<HTMLTextAreaElement>;
  tocText: string;
  setTocText: (s: string) => void;
  outcomeRef: React.RefObject<HTMLTextAreaElement>;
  outcome: string;
  setOutcome: (s: string) => void;
  ideaRef: React.RefObject<HTMLDivElement>;
  selectedIdeaSource: DropdownOption;
  ideaSourceOptions: DropdownOption[];
  handleSelectIdeaSource: (opt: DropdownOption) => void;
  ratingRef: React.RefObject<HTMLDivElement>;
  selectedRating: DropdownOption;
  defaultRatingOptions: DropdownOption[];
  handleSelectRating: (opt: DropdownOption) => void;
  onCancel: () => void;
  onSave: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  isEditing?: boolean;
  actionContainerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Renders the add or edit test-of-change form with validation feedback and live character counts.
 * @returns The managed test-of-change form when visible.
 */
const ManageTestOfChange: React.FC<ManageTestOfChangeProps> = ({
  visible,
  validationErrors,
  titleRef,
  tocTitle,
  setTocTitle,
  tocTextRef,
  tocText,
  setTocText,
  outcomeRef,
  outcome,
  setOutcome,
  ideaRef,
  selectedIdeaSource,
  ideaSourceOptions,
  handleSelectIdeaSource,
  ratingRef,
  selectedRating,
  defaultRatingOptions,
  handleSelectRating,
  onCancel,
  onSave,
  isEditing = false,
  actionContainerRef,
}) => {
  const fieldErrors = React.useMemo(() => {
    return validationErrors.reduce<Record<string, string>>((acc, error) => {
      if (!acc[error.field]) {
        acc[error.field] = error.message;
      }
      return acc;
    }, {});
  }, [validationErrors]);
  if (!visible) return null;

  const FIELD_MAX = 1024;
  const FIELD_MAX_TITILE = 30;
  const titleRemaining = Math.max(0, FIELD_MAX_TITILE - (tocTitle ? tocTitle.length : 0));
  const actionButtons = (
    <>
      <button className="button py-0! -inverted" onClick={onCancel}>Cancel</button>
      <button className="button py-0! -primary -light" onClick={(e) => onSave(e)}>Save</button>
    </>
  );

  return (
    <div className=" w-full rounded-xl  mt-8! mb-24! ds-form">
      {validationErrors.length > 0 && (
        <div className="mb-4!">
          <ErrorBanner errors={validationErrors} />
        </div>
      )}
      <div className="pt-4 flex gap-4 mb-12!">
        <p className="p1"><strong>{isEditing ? 'Edit Test of Change' : 'Add a new Test'}</strong></p>
      </div>
      <div className=" w-full!" data-ds-theme="vessel" data-ds-variant="input">
        <label htmlFor="toc-title">Title<span aria-hidden="true" className="text-red-600 ml-1">*</span></label>
        <input ref={titleRef} id="toc-title" type="text" value={tocTitle} onChange={(e) => setTocTitle(e.target.value.slice(0, FIELD_MAX_TITILE))} className="mt-1! bg-white min-w-163! border border-border-light rounded-md p-2" maxLength={FIELD_MAX} style={{ textTransform: 'none' }} />
        {fieldErrors.title && (
          <ValidationErrorDisplay message={fieldErrors.title} id="error-title" />
        )}
        <output className="ds-form__text-input-helper mt-1" aria-live="polite">
          {`${titleRemaining} character left`}
        </output>
      </div>

      <div className="flex w-full! gap-x-8 mt-8! mb-8!">
        <div className="w-1/2!">
          <label htmlFor="toc-text">Test Of Change<span aria-hidden="true" className="text-red-600 ml-1">*</span></label>
          <div className="textarea-wrapper w-full relative">
            <textarea ref={tocTextRef} id="toc-text" rows={2} value={tocText} onChange={(e) => setTocText(e.target.value.slice(0, FIELD_MAX))} className="text-area_word-count text-area_global -book text-base! text-[#0D1C3D] w-full! w-full min-w-0 block" style={{ width: '100%', textTransform: 'none' }} maxLength={FIELD_MAX} />
            {fieldErrors.testOfChange && (
              <ValidationErrorDisplay message={fieldErrors.testOfChange} id="error-test-of-change" />
            )}
            <output className="ds-form__text-input-helper mt-1" aria-live="polite">
              {getRemainingCharacterText(tocText, FIELD_MAX)}
            </output>
          </div>
        </div>
        <div className="w-1/2!">
          <label htmlFor="toc-outcome">OutCome<span aria-hidden="true" className="text-red-600 ml-1">*</span></label>
          <div className="textarea-wrapper w-full relative">
            <textarea ref={outcomeRef} id="toc-outcome" rows={2} value={outcome} onChange={(e) => setOutcome(e.target.value.slice(0, FIELD_MAX))} className="text-area_word-count text-area_global -book text-base! text-[#0D1C3D] w-full! w-full min-w-0 block" style={{ width: '100%', textTransform: 'none' }} maxLength={FIELD_MAX} />
            {fieldErrors.outcome && (
              <ValidationErrorDisplay message={fieldErrors.outcome} id="error-outcome" />
            )}
            <output className="ds-form__text-input-helper mt-1" aria-live="polite">
              {getRemainingCharacterText(outcome, FIELD_MAX)}
            </output>
          </div>
        </div>
      </div>

      <div className="flex w-1/2! gap-x-8 mt-8! mb-8!">
        <div className="w-1/2!">
          <label htmlFor="idea-source" className="ds-dropdown__label">
            <span className="ds-dropdown__label-text">Where did you get this idea ?<span aria-hidden="true" className="text-red-600 ml-1">*</span></span>
          </label>
          <div className="textarea-wrapper w-full relative">
            <div ref={ideaRef} id="idea-source">
              <Dropdown
                selectedValue={selectedIdeaSource}
                options={ideaSourceOptions}
                onSelect={handleSelectIdeaSource}
                buttonClassName="w-full! bg-white"
                placeholder="Select"
              />
            </div>
            {fieldErrors.ideaSource && (
              <ValidationErrorDisplay message={fieldErrors.ideaSource} id="error-idea-source" />
            )}
          </div>
        </div>
        <div className="w-1/2!">
          <label htmlFor="rating" className="ds-dropdown__label">
            <span className="ds-dropdown__label-text">Rating<span aria-hidden="true" className="text-red-600 ml-1">*</span></span>
          </label>
          <div ref={ratingRef} id="rating">
            <Dropdown
              selectedValue={selectedRating}
              options={defaultRatingOptions}
              onSelect={handleSelectRating}
              buttonClassName="w-full! bg-white"
              menuClassName="w-full! max-h-65! overflow-y-auto!"
              placeholder="Select"
            />
            {fieldErrors.rating && (
              <ValidationErrorDisplay message={fieldErrors.rating} id="error-rating" />
            )}
          </div>
        </div>
      </div>

      {actionContainerRef?.current
        ? createPortal(actionButtons, actionContainerRef.current)
        : <div className="flex justify-end gap-x-4 mt-4!">{actionButtons}</div>}
    </div>
  );
};

export default ManageTestOfChange;
